import { IconAdjustments } from '@tabler/icons-react-native';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { Event, track } from 'app/lib/analytics';
import { ListingType } from 'app/types/listing';
import { ActivityIndicator, View } from 'react-native';
import { HubButton, HubCard, HubCopy, HubEmoji, hubColors, styles } from '../components';

import type { OfferHubState } from '../hooks';

export function OfferHubCompletion({
  t,
  accepted,
  navigation,
  statistics,
  refreshStatistics,
  pending,
  completedLists,
}: Pick<
  OfferHubState,
  't' | 'accepted' | 'navigation' | 'statistics' | 'refreshStatistics' | 'pending' | 'completedLists'
>) {
  return (
    <HubCard>
      <View style={styles.compactCopy}>
        <HubEmoji>👏</HubEmoji>
        <View style={styles.completionCopy}>
          <HubCopy title={t('listComplete')} />
          {accepted.length > 0 ? (
            <Text style={styles.completionLabel} color="text.secondary" textAlign="center">
              {t('published', { count: accepted.length })}
            </Text>
          ) : null}
        </View>
        {accepted.length ? (
          <View style={styles.thumbnails}>
            {accepted.slice(0, 3).map((decision) => (
              <PCImage
                key={decision.id}
                source={{ uri: decision.picture }}
                contentFit="cover"
                style={styles.thumbnail}
              />
            ))}
            {accepted.length > 3 ? (
              <View
                style={[
                  styles.thumbnail,
                  { backgroundColor: hubColors.mint, alignItems: 'center', justifyContent: 'center' },
                ]}
              >
                <Text fontWeight="600" style={{ color: hubColors.ink }}>
                  +{accepted.length - 3}
                </Text>
              </View>
            ) : null}
          </View>
        ) : null}
        <HubButton
          size="small"
          tone="outline"
          prefix={<IconAdjustments size={20} color={hubColors.ink} />}
          onPress={() => {
            track(Event.OfferHubManageOffersPressed);
            navigation.navigate(
              'main',
              {
                screen: 'management',
                params: { screen: 'my-listings', params: { type: ListingType.offer }, initial: false },
              },
              { pop: true },
            );
          }}
        >
          {t('manageOffers')}
        </HubButton>
      </View>
      <View style={styles.stats}>
        <Text variant="bodySmall" color="text.secondary" textAlign="center">
          {t('lifetimeStats')}
        </Text>
        <View style={styles.stat}>
          {statistics.data?.listings.offers ? (
            <Text style={styles.title} fontWeight="600">
              {statistics.data.listings.offers.total}
            </Text>
          ) : statistics.isError ? (
            <HubButton
              tone="ghost"
              onPress={() => {
                void refreshStatistics();
              }}
            >
              {t('retry')}
            </HubButton>
          ) : (
            <ActivityIndicator color={hubColors.teal} />
          )}
          <Text style={styles.body} color="text.secondary">
            {t('publishedLabel')}
          </Text>
        </View>
        {completedLists !== undefined ? (
          <View style={styles.stat}>
            <Text style={styles.title} fontWeight="600">
              {completedLists}
            </Text>
            <Text style={styles.body} color="text.secondary" textAlign="center">
              {t('completedLists', { count: completedLists })}
            </Text>
          </View>
        ) : null}
        {pending > 0 ? (
          <Text accessibilityLiveRegion="polite" variant="bodySmall" color="text.secondary" textAlign="center">
            {t('pendingDescription')}
          </Text>
        ) : null}
      </View>
    </HubCard>
  );
}
